import { environment } from "src/environments/environment";
import type {
  EngageReferral,
  EngageReferralsResponse,
} from "../services/engage-activity.service";

/** PHI list endpoint used in place of Engage `GET /referrals` for clinic-v3 worklist / Engage list. */
export function phiPatientDirectoryListUrl(): string {
  return `${environment.apiBaseUrl}api/v1/patients/directory`;
}

/** Paginated GET target: `page` / `limit` on the query string (PHI `apiBaseUrl`, not Engage). */
export function phiPatientDirectoryListGetUrl(page: number, limit: number): string {
  try {
    const u = new URL(phiPatientDirectoryListUrl());
    u.searchParams.set("page", String(page));
    u.searchParams.set("limit", String(limit));
    return appendPhiDirectoryQueryParamMirrors(u.toString());
  } catch {
    return `${environment.apiBaseUrl}api/v1/patients/directory?page=${encodeURIComponent(
      String(page)
    )}&limit=${encodeURIComponent(String(limit))}`;
  }
}

/** Values merged onto a directory GET URL as query params (arrays → CSV; `full_names` → one `full_name`; `engage_search` → fuzzy patient search; `engage_search_full` → full rows with engage_search). */
export type PatientDirectoryFilterValues = Record<
  string,
  string | number | boolean | string[] | undefined | null
>;

/** PHI directory `referral_source` query token for rows with no referral source. */
export const PATIENT_DIRECTORY_REFERRAL_SOURCE_NO_SOURCE = "no_source";

export const PATIENT_DIRECTORY_FACET_NO_VALUE = {
  queue: "no_queue",
  patientStatus: "no_patient_status",
  lead_status: "no_patient_status",
  encounter_status: "no_status",
  last_encounter_status: "no_status",
  visit_type: "no_visit_type",
  service: "no_service",
  schedule_resource: "no_resource",
  location: "no_location",
} as const;

/** PHI directory `dob` query token for rows with no date of birth. */
export const PATIENT_DIRECTORY_DOB_NO_VALUE = "no_dob";
/** PHI directory `last_dos` query token for rows with no recent DOS. */
export const PATIENT_DIRECTORY_LAST_DOS_NO_VALUE = "no_dos";

/**
 * Maps Engage / worklist source facet values to PHI directory `referral_source` query tokens.
 * Engage Source `--` sends `no_source` (worksheet blank facet uses the same directory token).
 */
export function patientDirectoryReferralSourceQueryValue(
  sourceFilterValue: string | null | undefined
): string {
  const v = String(sourceFilterValue ?? "").trim().toLowerCase();
  if (!v) {
    return "";
  }
  if (
    v === PATIENT_DIRECTORY_REFERRAL_SOURCE_NO_SOURCE ||
    v === "--" ||
    v === "blank" ||
    v === "__worksheet_blank_source__"
  ) {
    return PATIENT_DIRECTORY_REFERRAL_SOURCE_NO_SOURCE;
  }
  return v;
}

function patientDirectoryFacetBlankQueryValue(
  directoryToken: string,
  internalSentinel: string,
  filterValue: string | null | undefined
): string {
  const v = String(filterValue ?? "").trim();
  if (!v) {
    return "";
  }
  const vl = v.toLowerCase();
  if (
    vl === directoryToken ||
    v === "--" ||
    v === "blank" ||
    v === internalSentinel
  ) {
    return directoryToken;
  }
  return v;
}

export function patientDirectoryQueIdQueryValue(value: string | null | undefined): string {
  return patientDirectoryFacetBlankQueryValue(
    PATIENT_DIRECTORY_FACET_NO_VALUE.queue,
    "__worksheet_blank_queue__",
    value
  );
}

export function patientDirectoryLeadStatusQueryValue(value: string | null | undefined): string {
  return patientDirectoryFacetBlankQueryValue(
    PATIENT_DIRECTORY_FACET_NO_VALUE.lead_status,
    "__worksheet_blank_patientStatus__",
    value
  );
}

export function patientDirectoryEncounterStatusQueryValue(
  value: string | null | undefined
): string {
  return patientDirectoryFacetBlankQueryValue(
    PATIENT_DIRECTORY_FACET_NO_VALUE.encounter_status,
    "__worksheet_blank_encStatus__",
    value
  );
}

/** Worklist Encounter Status column → PHI `GET …/patients/directory?encounter_status=…`. */
export function patientDirectoryLastEncounterStatusQueryValue(
  value: string | null | undefined
): string {
  return patientDirectoryFacetBlankQueryValue(
    PATIENT_DIRECTORY_FACET_NO_VALUE.last_encounter_status,
    "__worksheet_blank_encStatus__",
    value
  );
}

export function patientDirectoryVisitTypeQueryValue(value: string | null | undefined): string {
  return patientDirectoryFacetBlankQueryValue(
    PATIENT_DIRECTORY_FACET_NO_VALUE.visit_type,
    "__worksheet_blank_visitType__",
    value
  );
}

export function patientDirectoryServiceQueryValue(value: string | null | undefined): string {
  return patientDirectoryFacetBlankQueryValue(
    PATIENT_DIRECTORY_FACET_NO_VALUE.service,
    "__worksheet_blank_service__",
    value
  );
}

export function patientDirectoryScheduleResourceQueryValue(
  value: string | null | undefined
): string {
  return patientDirectoryFacetBlankQueryValue(
    PATIENT_DIRECTORY_FACET_NO_VALUE.schedule_resource,
    "__worksheet_blank_resource__",
    value
  );
}

export function patientDirectoryLocationQueryValue(value: string | null | undefined): string {
  return patientDirectoryFacetBlankQueryValue(
    PATIENT_DIRECTORY_FACET_NO_VALUE.location,
    "__worksheet_blank_location__",
    value
  );
}

/**
 * PHI `GET …/patients/directory` **`assigned_to`** query string.
 * Comma-separated assignee user IDs plus optional `everyone` sentinel for unassigned rows.
 */
export function patientDirectoryAssignedToQueryValue(
  coordinatorOrAssignedCsv: string | null | undefined
): string | undefined {
  const raw = (coordinatorOrAssignedCsv ?? "").trim();
  if (!raw) {
    return undefined;
  }
  const tokens = raw.includes(",")
    ? raw.split(",").map((x) => x.trim()).filter(Boolean)
    : [raw];
  if (!tokens.length) {
    return undefined;
  }
  return tokens.join(",");
}

/**
 * Appends filter params to a directory list URL. Uses {@link appendPhiDirectoryQueryParamMirrors}
 * so gateways that bind only camelCase or only snake_case still receive aliases where configured.
 */
/**
 * PHI directory `location` — append after all other params so `URLSearchParams` does not
 * re-serialize spaces as `+`. Value is the display name with literal spaces (no `+`).
 */
export function appendPatientDirectoryLocationQuery(
  url: string,
  locationName: string
): string {
  const trimmed = locationName.trim();
  if (!trimmed) {
    return url;
  }
  let base = url;
  try {
    const u = new URL(url);
    u.searchParams.delete("locations");
    u.searchParams.delete("location");
    base = u.toString();
  } catch {
    /* keep url */
  }
  const sep = base.includes("?") ? "&" : "?";
  return `${base}${sep}location=${trimmed}`;
}

export function appendPatientDirectoryFiltersToUrl(
  url: string,
  filters: PatientDirectoryFilterValues,
  options?: { mirrorQueryParams?: boolean }
): string {
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    return url;
  }
  /** Applied last — `searchParams.set` after manual `location=` would turn spaces into `+`. */
  let locationQueryValue: string | null = null;
  for (const [key, raw] of Object.entries(filters)) {
    if (raw == null || raw === "") {
      continue;
    }
    /** Legacy plural key — PHI directory expects singular `location` with display names. */
    if (key === "locations") {
      continue;
    }
    if (key === "location" && typeof raw === "string") {
      const trimmed = raw.trim();
      if (trimmed) {
        locationQueryValue = trimmed;
      }
      continue;
    }
    if (Array.isArray(raw)) {
      if (key === "full_names") {
        const names = raw.map((x) => String(x ?? "").trim()).filter(Boolean);
        if (names.length) {
          u.searchParams.set("full_name", names.join(","));
        }
        continue;
      }
      const csv = raw.map((x) => String(x ?? "").trim()).filter(Boolean).join(",");
      if (csv) {
        u.searchParams.set(key, csv);
      }
      continue;
    }
    if (typeof raw === "boolean") {
      u.searchParams.set(key, raw ? "true" : "false");
      continue;
    }
    u.searchParams.set(key, String(raw));
  }
  u.searchParams.delete("locations");
  let out =
    options?.mirrorQueryParams === false
      ? u.toString()
      : appendPhiDirectoryQueryParamMirrors(u.toString());
  if (locationQueryValue) {
    out = appendPatientDirectoryLocationQuery(out, locationQueryValue);
  }
  return out;
}

/**
 * Some gateways only bind camelCase or only snake_case query keys. Duplicate known pairs when one side is present.
 */
export function appendPhiDirectoryQueryParamMirrors(url: string): string {
  try {
    const u = new URL(url);
    const params = u.searchParams;
    const pairs: ReadonlyArray<readonly [string, string]> = [
      ["patientId", "patient_id"],
      ["patient_id", "patientId"],
      ["que_id", "queId"],
      ["queId", "que_id"],
      ["engage_search_full", "engageSearchFull"],
      ["engageSearchFull", "engage_search_full"],
    ];
    for (const [a, b] of pairs) {
      const v = params.get(a);
      if (v != null && v !== "" && !params.has(b)) {
        params.set(b, v);
      }
    }
    return u.toString();
  } catch {
    return url;
  }
}

/** Max nodes visited while searching for a row array (avoids runaway on huge JSON). */
const DEEP_SCAN_MAX_NODES = 400;
const DEEP_SCAN_MAX_DEPTH = 8;

function isPlainObjectRecord(x: unknown): x is Record<string, unknown> {
  return x != null && typeof x === "object" && !Array.isArray(x);
}

/**
 * When the API nests the list unpredictably, find the first non-empty array whose
 * first element is a plain object (typical for directory / referral rows).
 */
function findFirstDeepObjectArray(node: unknown): EngageReferral[] | null {
  let visited = 0;
  const scan = (n: unknown, depth: number): EngageReferral[] | null => {
    if (n == null || visited++ > DEEP_SCAN_MAX_NODES || depth > DEEP_SCAN_MAX_DEPTH) {
      return null;
    }
    if (Array.isArray(n)) {
      if (n.length === 0) {
        return null;
      }
      const first = n[0];
      if (first != null && typeof first === "object" && !Array.isArray(first)) {
        return n as EngageReferral[];
      }
      return null;
    }
    if (!isPlainObjectRecord(n)) {
      return null;
    }
    for (const v of Object.values(n)) {
      const hit = scan(v, depth + 1);
      if (hit) {
        return hit;
      }
    }
    return null;
  };
  return scan(node, 0);
}

/**
 * PHI directory rows often nest encounter / schedule fields under `patient`, `encounter`,
 * `latestEncounter`, etc. Pull scalars up so worklist mapping reads DOS, status, visit type,
 * resource, referral source, and location from the same shapes as flat referrals.
 */
export function expandPatientDirectoryListRow(row: EngageReferral): EngageReferral {
  if (row == null || typeof row !== "object") {
    return row;
  }
  const root = row as Record<string, unknown>;
  const out: Record<string, unknown> = { ...root };

  /** Do not copy referral / encounter primary ids from nested blobs onto root (mapper uses root identity). */
  const skipNestedScalarKey = (k: string): boolean =>
    k === "id" ||
    k === "referralId" ||
    k === "referral_id" ||
    k === "referralID" ||
    k === "referralUuid" ||
    k === "referral_uuid";

  const nestedContainers: readonly string[] = [
    "patient",
    "Patient",
    "referral",
    "Referral",
    "latestEncounter",
    "latest_encounter",
    "LatestEncounter",
    "encounter",
    "Encounter",
    "latestScheduledEncounter",
    "latest_scheduled_encounter",
    "appointment",
    "Appointment",
    "schedule",
    "encounterSchedule",
    "encounter_schedule",
    "scheduledEncounter",
    "scheduled_encounter",
    "directoryEncounter",
    "directory_encounter",
    "phiEncounter",
    "phi_encounter",
    "referralDetails",
    "referral_details",
    "workFlow",
    "workflow",
    "WorkFlow",
    "pipeline",
    "biller_lookup",
    "billerLookup",
    "BillerLookup",
  ];

  const pullNestedScalars = (node: unknown, depth: number): void => {
    if (depth <= 0 || node == null || typeof node !== "object" || Array.isArray(node)) {
      return;
    }
    const o = node as Record<string, unknown>;
    for (const [k, v] of Object.entries(o)) {
      if (skipNestedScalarKey(k)) {
        continue;
      }
      if (v != null && typeof v === "object" && !Array.isArray(v)) {
        pullNestedScalars(v, depth - 1);
        continue;
      }
      if (v == null || v === "") {
        continue;
      }
      const cur = out[k];
      const curEmpty =
        cur == null || (typeof cur === "string" && String(cur).trim() === "");
      if (curEmpty) {
        out[k] = v;
      }
    }
  };

  for (const key of nestedContainers) {
    pullNestedScalars(root[key], 4);
  }

  if (out["last_activity"] == null && out["lastActivity"] != null) {
    out["last_activity"] = out["lastActivity"];
  }
  if (out["last_activity"] == null) {
    for (const key of nestedContainers) {
      const node = root[key];
      if (node == null || typeof node !== "object" || Array.isArray(node)) {
        continue;
      }
      const nested = node as Record<string, unknown>;
      const la = nested["last_activity"] ?? nested["lastActivity"];
      if (la != null && typeof la === "object") {
        out["last_activity"] = la;
        break;
      }
    }
  }

  return out as EngageReferral;
}

function mergeReferralRecordsFillEmptyScalars(
  base: EngageReferral,
  fill: EngageReferral
): EngageReferral {
  const b = base as Record<string, unknown>;
  const f = fill as Record<string, unknown>;
  const out: Record<string, unknown> = { ...b };
  for (const [k, v] of Object.entries(f)) {
    if (v == null || v === "") {
      continue;
    }
    if (typeof v === "object" && !Array.isArray(v)) {
      continue;
    }
    const cur = out[k];
    const curEmpty =
      cur == null || (typeof cur === "string" && String(cur).trim() === "");
    if (curEmpty) {
      out[k] = v;
    }
  }
  return out as EngageReferral;
}

/**
 * When the worklist loads Engage `GET /referrals`, overlay PHI directory rows for the same page
 * so DOS / encounter / visit type / resource / referral source match directory projection.
 */
export function mergeEngageReferralPageWithPatientDirectoryOverlay(
  engageRows: EngageReferral[],
  directoryRows: EngageReferral[]
): EngageReferral[] {
  if (!engageRows?.length || !directoryRows?.length) {
    return engageRows?.length ? engageRows.map((r) => expandPatientDirectoryListRow(r)) : [];
  }
  const dirExpanded = directoryRows.map((d) => expandPatientDirectoryListRow(d));
  const dirByRefId = new Map<string, EngageReferral>();
  const dirByPatientId = new Map<string, EngageReferral[]>();
  for (const d of dirExpanded) {
    const r = d as Record<string, unknown>;
    const rid = String(r.id ?? r.referralId ?? r.referral_id ?? "").trim().toLowerCase();
    if (rid) {
      dirByRefId.set(rid, d);
    }
    const pid = String(
      r.patientId ?? r.patient_id ?? r["PatientId"] ?? ""
    )
      .trim()
      .toLowerCase();
    if (pid) {
      const arr = dirByPatientId.get(pid) ?? [];
      arr.push(d);
      dirByPatientId.set(pid, arr);
    }
  }

  return engageRows.map((raw) => {
    const e = expandPatientDirectoryListRow(raw);
    const er = e as Record<string, unknown>;
    const rid = String(er.id ?? er.referralId ?? er.referral_id ?? "").trim().toLowerCase();
    let dirHit: EngageReferral | undefined = rid ? dirByRefId.get(rid) : undefined;
    if (!dirHit) {
      const pid = String(
        er.patientId ?? er.patient_id ?? er["PatientId"] ?? ""
      )
        .trim()
        .toLowerCase();
      const cands = pid ? dirByPatientId.get(pid) : undefined;
      if (cands?.length === 1) {
        dirHit = cands[0];
      }
    }
    if (!dirHit) {
      return e;
    }
    return mergeReferralRecordsFillEmptyScalars(e, dirHit);
  });
}

/**
 * Directory rows sometimes omit `id` / `referralId` but carry `referral_id` or only `patientId`.
 * Downstream (Engage sidebar filters, worklist merge) expects a stable referral row id.
 */
function coerceDirectoryListRow(row: EngageReferral): EngageReferral {
  const expanded = expandPatientDirectoryListRow(row);
  const r = expanded as Record<string, unknown>;
  const merged = { ...r };
  if (merged.lead_status == null && merged.leadStatus != null) {
    merged.lead_status = merged.leadStatus;
  }
  if (merged.last_activity == null && merged.lastActivity != null) {
    merged.last_activity = merged.lastActivity;
  }
  const existing = String(
    merged["id"] ?? merged["referralId"] ?? merged["referral_id"] ?? merged["referralID"] ?? ""
  ).trim();
  if (existing) {
    if (!String(merged["id"] ?? "").trim()) {
      merged["id"] = existing;
    }
    return merged as EngageReferral;
  }
  const encounterId = String(
    merged["latestEncounterId"] ??
      merged["latest_encounter_id"] ??
      merged["encounterId"] ??
      merged["encounter_id"] ??
      ""
  ).trim();
  if (encounterId) {
    const patientId = String(
      merged["patientId"] ?? merged["patient_id"] ?? merged["PatientId"] ?? ""
    ).trim();
    // Row `id` stays encounter-scoped for multi-encounter directory lists; Engage PATCH targets use `referralId`.
    return {
      ...merged,
      id: encounterId,
      ...(patientId ? { referralId: patientId } : {}),
    } as EngageReferral;
  }
  const fallback = String(
    merged["referralUuid"] ??
      merged["referral_uuid"] ??
      merged["patientId"] ??
      merged["patient_id"] ??
      merged["PatientId"] ??
      ""
  ).trim();
  if (!fallback) {
    return merged as EngageReferral;
  }
  return { ...merged, id: fallback, referralId: fallback } as EngageReferral;
}

function pickFirstReferralArrayFromRecord(obj: Record<string, unknown>): EngageReferral[] | null {
  const keys = [
    "items",
    "Items",
    "referrals",
    "Referrals",
    "patients",
    "Patients",
    "records",
    "Records",
    "rows",
    "Rows",
    "results",
    "Results",
    "entities",
    "referralList",
    "directory",
    "list",
    "List",
    "value",
    "content",
    "Content",
  ];
  for (const k of keys) {
    const v = obj[k];
    if (Array.isArray(v) && v.length > 0) {
      return v as EngageReferral[];
    }
  }
  for (const k of keys) {
    const v = obj[k];
    if (Array.isArray(v)) {
      return v as EngageReferral[];
    }
  }
  return null;
}

function extractReferralsRows(res: unknown): EngageReferral[] {
  if (res == null) {
    return [];
  }
  if (Array.isArray(res)) {
    return res as EngageReferral[];
  }
  if (typeof res !== "object") {
    return [];
  }
  const r = res as Record<string, unknown>;

  const fromRoot = pickFirstReferralArrayFromRecord(r);
  if (fromRoot) {
    return fromRoot;
  }

  const data = r["data"];
  if (Array.isArray(data)) {
    return data as EngageReferral[];
  }
  if (data != null && typeof data === "object" && !Array.isArray(data)) {
    const d = data as Record<string, unknown>;
    const nested = pickFirstReferralArrayFromRecord(d);
    if (nested) {
      return nested;
    }
    const inner = d["data"];
    if (Array.isArray(inner)) {
      return inner as EngageReferral[];
    }
  }

  for (const wrap of ["result", "Result", "payload", "Payload", "body", "Body"]) {
    const w = r[wrap];
    if (Array.isArray(w)) {
      return w as EngageReferral[];
    }
    if (w != null && typeof w === "object" && !Array.isArray(w)) {
      const wr = w as Record<string, unknown>;
      const hit = pickFirstReferralArrayFromRecord(wr);
      if (hit) {
        return hit;
      }
      const d2 = wr["data"];
      if (Array.isArray(d2)) {
        return d2 as EngageReferral[];
      }
      if (d2 != null && typeof d2 === "object" && !Array.isArray(d2)) {
        const n2 = pickFirstReferralArrayFromRecord(d2 as Record<string, unknown>);
        if (n2) {
          return n2;
        }
      }
    }
  }

  const deep = findFirstDeepObjectArray(res);
  if (deep?.length) {
    return deep;
  }

  return [];
}

function numOrUndef(v: unknown): number | undefined {
  if (v == null) {
    return undefined;
  }
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

function normalizePatientDirectoryFacetEntry(item: unknown): string {
  if (item == null) {
    return "";
  }
  if (typeof item === "string" || typeof item === "number" || typeof item === "boolean") {
    return String(item).trim();
  }
  if (typeof item === "object" && !Array.isArray(item)) {
    const o = item as Record<string, unknown>;
    /** Grouped `include_*` facets may use explicit null for blank / unassigned buckets. */
    if ("encounter_status" in o && o["encounter_status"] == null) {
      return PATIENT_DIRECTORY_FACET_NO_VALUE.encounter_status;
    }
    if ("last_encounter_status" in o && o["last_encounter_status"] == null) {
      return PATIENT_DIRECTORY_FACET_NO_VALUE.last_encounter_status;
    }
    if ("assigned_to" in o && o["assigned_to"] == null) {
      return "everyone";
    }
    if ("lead_status" in o && o["lead_status"] == null) {
      return PATIENT_DIRECTORY_FACET_NO_VALUE.lead_status;
    }
    if ("referral_source" in o && o["referral_source"] == null) {
      return PATIENT_DIRECTORY_REFERRAL_SOURCE_NO_SOURCE;
    }
    if ("que_id" in o && o["que_id"] == null) {
      return PATIENT_DIRECTORY_FACET_NO_VALUE.queue;
    }
    if ("last_service" in o && o["last_service"] == null) {
      return PATIENT_DIRECTORY_FACET_NO_VALUE.service;
    }
    const raw =
      o["value"] ??
      o["assigned_to"] ??
      o["assignedTo"] ??
      o["referral_source"] ??
      o["referralSource"] ??
      o["source"] ??
      o["lead_status"] ??
      o["leadStatus"] ??
      o["patient_status"] ??
      o["patientStatus"] ??
      o["encounter_status"] ??
      o["encounterStatus"] ??
      o["last_encounter_status"] ??
      o["lastEncounterStatus"] ??
      o["last_service"] ??
      o["lastService"] ??
      o["service"] ??
      o["work_flow_id"] ??
      o["workFlowId"] ??
      o["workflow_id"] ??
      o["workflowId"] ??
      o["workflow"] ??
      o["workFlow"] ??
      o["que_id"] ??
      o["queId"] ??
      o["stage_id"] ??
      o["stageId"] ??
      o["stage"] ??
      o["label"] ??
      o["name"] ??
      o["id"];
    if (raw == null) {
      return "";
    }
    return String(raw).trim();
  }
  return "";
}

/**
 * Distinct facet values from directory `include_*` responses (`facetValues`, `last_services`, …).
 */
export function extractPatientDirectoryFacetValues(res: unknown): string[] {
  if (res == null || typeof res !== "object") {
    return [];
  }
  const r = res as Record<string, unknown>;
  const dataRec =
    r["data"] != null && typeof r["data"] === "object" && !Array.isArray(r["data"])
      ? (r["data"] as Record<string, unknown>)
      : undefined;
  const keys = [
    "facetValues",
    "facet_values",
    "last_services",
    "lastServices",
    "services",
    "Services",
    "encounter_statuses",
    "encounterStatuses",
    "encounter_status",
    "encounterStatus",
    "last_encounter_statuses",
    "lastEncounterStatuses",
    "last_encounter_status",
    "lastEncounterStatus",
    "assigned_tos",
    "assignedTos",
    "assigned_to",
    "assignedTo",
    "sources",
    "Sources",
    "referral_sources",
    "referralSources",
    "source",
    "patient_statuses",
    "patientStatuses",
    "lead_statuses",
    "leadStatuses",
    "patient_status",
    "patientStatus",
    "lead_status",
    "leadStatus",
    "workflows",
    "workFlows",
    "work_flows",
    "workflow_ids",
    "workflowIds",
    "stages",
    "Stages",
    "stage_ids",
    "stageIds",
    "stage",
    "que_ids",
    "queIds",
    "que_id",
    "queId",
  ];
  const seen = new Set<string>();
  const out: string[] = [];
  const push = (raw: string): void => {
    const v = raw.trim();
    if (!v) {
      return;
    }
    const key = v.toLowerCase();
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    out.push(v);
  };
  for (const key of keys) {
    for (const source of [r, dataRec]) {
      if (!source) {
        continue;
      }
      const val = source[key];
      if (!Array.isArray(val)) {
        continue;
      }
      for (const item of val) {
        push(normalizePatientDirectoryFacetEntry(item));
      }
    }
  }
  return out;
}

/**
 * Normalizes PHI `GET …/patients/directory` (or legacy Engage referrals list) JSON into {@link EngageReferralsResponse}.
 * `page` and `limit` are the request parameters (used for `hasMore` when `total` is unknown).
 */
export function parsePatientDirectoryOrLegacyReferralsPayload(
  res: unknown,
  page: number,
  limit: number
): EngageReferralsResponse {
  const rows = extractReferralsRows(res).map((row) => coerceDirectoryListRow(row));
  const r = res != null && typeof res === "object" ? (res as Record<string, unknown>) : {};
  const dataRec =
    r["data"] != null && typeof r["data"] === "object" && !Array.isArray(r["data"])
      ? (r["data"] as Record<string, unknown>)
      : undefined;
  const totalRaw =
    r["total"] ??
    r["totalCount"] ??
    r["totalRows"] ??
    dataRec?.["total"] ??
    dataRec?.["totalCount"] ??
    (r["pagination"] as Record<string, unknown> | undefined)?.["total"] ??
    (r["meta"] as Record<string, unknown> | undefined)?.["total"];
  const total = numOrUndef(totalRaw);
  const limitNum = Number(limit);
  const pageNum = Number(page);
  const hasMore =
    total != null
      ? pageNum * limitNum < total
      : rows.length >= limitNum;

  const fc = r["filterCounts"] as Record<string, unknown> | undefined;

  return {
    referrals: rows,
    total: total ?? undefined,
    page: Number.isFinite(pageNum) ? pageNum : page,
    hasMore,
    missedCount: numOrUndef(r["missedCount"] ?? fc?.["missed"]),
    smsCount: numOrUndef(r["smsCount"] ?? fc?.["sms"] ?? fc?.["message"]),
    voicemailCount: numOrUndef(r["voicemailCount"] ?? fc?.["voicemail"]),
    reminderCount: numOrUndef(r["reminderCount"] ?? fc?.["reminder"]),
    newLeadsCount: numOrUndef(r["newLeadsCount"] ?? fc?.["new_lead"] ?? fc?.["newLead"]),
    totalNotifications: numOrUndef(r["totalNotifications"] ?? r["total_notifications"]),
  };
}

export interface EngagePipelineGroupedStageTotals {
  grandTotal: number;
  stages: Array<{
    stageId: string | null;
    total: number;
    totalPages: number;
  }>;
}

/** PHI `include_stage=true` + `workFlowId`: stage bucket totals (same filters as list directory). */
export function parsePatientDirectoryGroupedByStagePayload(
  res: unknown
): EngagePipelineGroupedStageTotals | null {
  const r =
    res != null && typeof res === "object" ? (res as Record<string, unknown>) : null;
  if (!r || r["groupByStage"] !== true) {
    return null;
  }
  const grandTotal = numOrUndef(r["grandTotal"]) ?? 0;
  const stagesRaw = r["stages"];
  if (!Array.isArray(stagesRaw)) {
    return { grandTotal, stages: [] };
  }
  const stages = stagesRaw.map((row) => {
    const o =
      row != null && typeof row === "object"
        ? (row as Record<string, unknown>)
        : {};
    const stageIdRaw = o["stageId"] ?? o["stage_id"];
    const stageId =
      stageIdRaw == null || String(stageIdRaw).trim() === ""
        ? null
        : String(stageIdRaw).trim();
    return {
      stageId,
      total: numOrUndef(o["total"]) ?? 0,
      totalPages: numOrUndef(o["totalPages"] ?? o["total_pages"]) ?? 0,
    };
  });
  return { grandTotal, stages };
}
